import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Backpack, ArrowLeft, Plus, Trash2, Save, Check, X,
  FileText, Pill, ShoppingBag, Car, Package, AlertTriangle,
  Calendar, Clock, ChevronDown, ChevronUp, Edit, PawPrint,
  Droplets, Heart, Shield, Utensils, Home, Phone, FileDown,
  Stethoscope, Syringe, Thermometer, Scissors, Activity,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { C, DISPLAY, BODY, MONO } from "@/styles/editorial";

const B = {
  border: `1px solid ${C.navy}15`,
  shadow: "none",
  shadowSm: "none",
  font: BODY,
  mono: MONO,
};

// Helper function to normalize special characters for PDF
const normalizeForPDF = (text) => {
  if (!text) return '';
  
  let result = String(text);
  
  // Spanish characters to ASCII
  result = result
    .replace(/á/g, 'a').replace(/Á/g, 'A')
    .replace(/é/g, 'e').replace(/É/g, 'E')
    .replace(/í/g, 'i').replace(/Í/g, 'I')
    .replace(/ó/g, 'o').replace(/Ó/g, 'O')
    .replace(/ú/g, 'u').replace(/Ú/g, 'U')
    .replace(/ñ/g, 'n').replace(/Ñ/g, 'N')
    .replace(/ü/g, 'u').replace(/Ü/g, 'U')
    .replace(/¿/g, '').replace(/¡/g, '')
    .replace(/–/g, '-').replace(/—/g, '-')
    .replace(/"/g, '"').replace(/"/g, '"')
    .replace(/'/g, "'").replace(/'/g, "'")
    .replace(/…/g, '...')
    .replace(/°/g, 'o')
    .replace(/€/g, 'EUR')
    .replace(/•/g, '-');
  
  // Remove emojis and non-ASCII characters
  let cleaned = '';
  for (let i = 0; i < result.length; i++) {
    const code = result.charCodeAt(i);
    if (code <= 127) {
      cleaned += result[i];
    }
  }
  
  return cleaned;
};

// ============================================
// TIPOS DE KIT - BASADO EN READY.GOV Y CRUZ ROJA
// ============================================
const KIT_TYPES = [
  { 
    id: "casa", 
    label: "Kit Casa", 
    icon: "", 
    color: "#3b82f6",
    description: "Suministros para quedarse en casa 3-5 días",
    duration: "3-5 días"
  },
  { 
    id: "evacuacion", 
    label: "Kit Evacuación", 
    icon: "", 
    color: "#ef4444",
    description: "Kit portátil para evacuar rápidamente",
    duration: "72 horas"
  },
  { 
    id: "primeros_auxilios", 
    label: "Primeros Auxilios", 
    icon: "", 
    color: "#22c55e",
    description: "Kit médico de emergencia para animales",
    duration: "Permanente"
  },
];

// ============================================
// TIPOS DE ANIMAL - PARA ITEMS ESPECÍFICOS
// ============================================
const ANIMAL_TYPES = [
  { id: "perro", label: "Perro", icon: "" },
  { id: "gato", label: "Gato", icon: "" },
  { id: "ave", label: "Ave", icon: "" },
  { id: "conejo", label: "Conejo/Roedor", icon: "" },
  { id: "reptil", label: "Reptil", icon: "" },
  { id: "pez", label: "Peces", icon: "" },
  { id: "caballo", label: "Caballo/Equino", icon: "" },
  { id: "ganado", label: "Ganado", icon: "" },
];

const CATEGORIES = [
  { id: "documentacion", label: "Documentación", icon: FileText, color: "#3b82f6" },
  { id: "alimentacion", label: "Alimentación", icon: Utensils, color: "#22c55e" },
  { id: "agua", label: "Agua", icon: Droplets, color: "#06b6d4" },
  { id: "medicacion", label: "Medicación", icon: Pill, color: "#ef4444" },
  { id: "transporte", label: "Transporte", icon: Car, color: "#f59e0b" },
  { id: "higiene", label: "Higiene", icon: Droplets, color: "#8b5cf6" },
  { id: "seguridad", label: "Seguridad/Confort", icon: Shield, color: "#78716c" },
  { id: "primeros_auxilios", label: "Primeros Auxilios", icon: Stethoscope, color: "#dc2626" },
  { id: "otros", label: "Otros", icon: Package, color: "#64748b" },
];

// ============================================
// ITEMS PREDEFINIDOS POR TIPO DE KIT
// Basado en: Ready.gov, AVMA, Cruz Roja, FEMA, 
// Protección Civil España
// ============================================
const KIT_ITEMS = {
  // ========== KIT CASA (3-5 DÍAS) ==========
  casa: {
    documentacion: [
      { name: "Carpeta con toda la documentación del animal", essential: true },
      { name: "Cartilla sanitaria / pasaporte actualizado", essential: true },
      { name: "Certificado de vacunación vigente", essential: true },
      { name: "Número de microchip anotado en varios sitios", essential: true },
      { name: "Fotos recientes del animal (varias, diferentes ángulos)", essential: true },
      { name: "Contacto del veterinario habitual", essential: true },
      { name: "Contacto de veterinario de urgencias 24h", essential: true },
      { name: "Lista de medicamentos con dosis exactas", essential: true },
      { name: "Historial médico/alergias conocidas", essential: false },
      { name: "Póliza de seguro de animales", essential: false },
      { name: "Copia del DNI del tutor/a", essential: false },
    ],
    alimentacion: [
      { name: "Pienso/comida seca para 7 días mínimo", essential: true, hasExpiry: true },
      { name: "Comida húmeda de reserva (latas/sobres)", essential: true, hasExpiry: true },
      { name: "Snacks/premios para reducir estrés", essential: false, hasExpiry: true },
      { name: "Comedero extra (por si se rompe)", essential: false },
      { name: "Recipiente hermético para guardar pienso", essential: false },
    ],
    agua: [
      { name: "Agua embotellada 5L por animal/día (35L mínimo)", essential: true },
      { name: "Bebedero extra", essential: false },
      { name: "Recipientes para almacenar agua", essential: true },
    ],
    medicacion: [
      { name: "Medicación habitual para 15 días mínimo", essential: true, hasExpiry: true },
      { name: "Antiparasitarios internos y externos", essential: false, hasExpiry: true },
      { name: "Calmantes/ansiolíticos (recetados)", essential: false, hasExpiry: true },
      { name: "Colirios si usa habitualmente", essential: false, hasExpiry: true },
    ],
    higiene: [
      { name: "Arena para gatos (si aplica) - 2 semanas", essential: false },
      { name: "Empapadores/pañales suficientes", essential: false },
      { name: "Bolsas de basura resistentes (muchas)", essential: true },
      { name: "Desinfectante pet-friendly", essential: false },
      { name: "Toallitas húmedas para animales", essential: false },
    ],
    seguridad: [
      { name: "Linterna con pilas de repuesto", essential: true },
      { name: "Mantas térmicas de emergencia (varias)", essential: true },
      { name: "Manta familiar del animal (olor reconfortante)", essential: true },
      { name: "Jaula/transportín de respaldo", essential: false },
      { name: "Radio a pilas para seguir alertas", essential: true },
    ],
    otros: [
      { name: "Juguetes favoritos (reduce ansiedad)", essential: false },
      { name: "Cama/colchoneta del animal", essential: false },
      { name: "Spray de feromonas calmantes (Feliway/Adaptil)", essential: false },
    ],
  },

  // ========== KIT EVACUACIÓN (72 HORAS) ==========
  evacuacion: {
    documentacion: [
      { name: "Carpeta impermeable con documentos esenciales", essential: true },
      { name: "Cartilla sanitaria en bolsa zip", essential: true },
      { name: "Fotos del animal en el móvil Y impresas", essential: true },
      { name: "Tarjeta con número de microchip", essential: true },
      { name: "Lista de contactos de emergencia", essential: true },
      { name: "Copia de recetas médicas", essential: true },
    ],
    alimentacion: [
      { name: "Comida para 3-5 días (ligera, fácil de transportar)", essential: true, hasExpiry: true },
      { name: "Comedero plegable/portátil", essential: true },
      { name: "Abrelatas manual (si comida en lata)", essential: false },
      { name: "Snacks de alto valor calórico", essential: false, hasExpiry: true },
    ],
    agua: [
      { name: "Agua embotellada 2L por animal mínimo", essential: true },
      { name: "Bebedero portátil plegable", essential: true },
      { name: "Pastillas potabilizadoras (backup)", essential: false },
    ],
    medicacion: [
      { name: "Medicación esencial para 7 días", essential: true, hasExpiry: true },
      { name: "Jeringuillas/dosificadores si necesita", essential: false },
      { name: "Calmantes de emergencia (vet-aprobados)", essential: false, hasExpiry: true },
    ],
    transporte: [
      { name: "Transportín resistente y ventilado", essential: true },
      { name: "Correa resistente (no extensible)", essential: true },
      { name: "Collar con chapa ID + tu teléfono actual", essential: true },
      { name: "Arnés de seguridad para coche", essential: true },
      { name: "Bozal (obligatorio para algunas razas)", essential: false },
      { name: "Manta para dentro del transportín", essential: true },
      { name: "Funda impermeable para transportín", essential: false },
    ],
    higiene: [
      { name: "Bolsas para residuos (mínimo 20)", essential: true },
      { name: "Toallitas húmedas", essential: true },
      { name: "Arena portátil para gatos + bandeja plegable", essential: false },
      { name: "Empapadores (pack pequeño)", essential: false },
    ],
    seguridad: [
      { name: "Linterna pequeña con pilas", essential: true },
      { name: "Manta térmica de emergencia", essential: true },
      { name: "Silbato de emergencia", essential: false },
      { name: "Cinta adhesiva (multiusos)", essential: false },
      { name: "Rotulador permanente (para marcar)", essential: false },
    ],
    otros: [
      { name: "Juguete pequeño favorito", essential: false },
      { name: "Prenda con olor del dueño", essential: false },
      { name: "Bolsa/mochila resistente para todo", essential: true },
    ],
  },

  // ========== KIT PRIMEROS AUXILIOS PARA ANIMALES ==========
  // Basado en: Cruz Roja Americana, AVMA, ASPCA
  primeros_auxilios: {
    primeros_auxilios: [
      // HERIDAS Y CORTES
      { name: "Gasas estériles (paquete grande)", essential: true },
      { name: "Vendas elásticas autoadhesivas (vet-wrap)", essential: true },
      { name: "Vendas de gasa en rollo", essential: true },
      { name: "Esparadrapo hipoalergénico", essential: true },
      { name: "Tijeras de punta roma (seguras)", essential: true },
      { name: "Pinzas de acero inoxidable", essential: true },
      { name: "Guantes de látex/nitrilo desechables", essential: true },
      
      // LIMPIEZA Y DESINFECCIÓN
      { name: "Suero fisiológico estéril (500ml mínimo)", essential: true, hasExpiry: true },
      { name: "Clorhexidina diluida (desinfectante)", essential: true, hasExpiry: true },
      { name: "Agua oxigenada 3% (solo para limpiar)", essential: false, hasExpiry: true },
      { name: "Algodón estéril", essential: true },
      { name: "Bastoncillos de algodón", essential: false },
      
      // MEDICAMENTOS BÁSICOS
      { name: "Pomada antibiótica veterinaria", essential: true, hasExpiry: true },
      { name: "Pomada cicatrizante", essential: false, hasExpiry: true },
      { name: "Antihistamínico (dosis según vet)", essential: false, hasExpiry: true },
      { name: "Carbón activado (intoxicaciones - CONSULTAR VET)", essential: false, hasExpiry: true },
      
      // INSTRUMENTOS
      { name: "Termómetro digital rectal", essential: true },
      { name: "Jeringuillas sin aguja (dar medicación oral)", essential: true },
      { name: "Cortaúñas para animales", essential: false },
      { name: "Pinzas quitagarrapatas", essential: true },
      { name: "Linterna pequeña (revisar ojos/boca)", essential: true },
      { name: "Manta térmica de emergencia", essential: true },
      
      // INMOVILIZACIÓN
      { name: "Tablilla/férula improvisable", essential: false },
      { name: "Toalla grande (inmovilizar/envolver)", essential: true },
      { name: "Bozal de emergencia o tira de tela", essential: true },
      
      // INFORMACIÓN
      { name: "Tarjeta con constantes vitales normales", essential: true },
      { name: "Teléfono de urgencias veterinarias 24h", essential: true },
      { name: "Teléfono de control de intoxicaciones", essential: false },
      { name: "Manual básico de primeros auxilios", essential: false },
    ],
  },
};

// ============================================
// ITEMS ESPECÍFICOS POR TIPO DE ANIMAL
// ============================================
const ANIMAL_SPECIFIC_ITEMS = {
  perro: [
    { name: "Bozal adecuado a su tamaño/raza", category: "transporte", essential: false },
    { name: "Correa de paseo resistente", category: "transporte", essential: true },
    { name: "Bolsas para excrementos", category: "higiene", essential: true },
    { name: "Juguete mordedor resistente", category: "otros", essential: false },
  ],
  gato: [
    { name: "Arena para gatos (suficiente)", category: "higiene", essential: true },
    { name: "Bandeja de arena portátil/plegable", category: "higiene", essential: true },
    { name: "Rascador pequeño/portátil", category: "otros", essential: false },
    { name: "Feliway spray (calma estrés)", category: "medicacion", essential: false },
    { name: "Red/malla para ventanas (prevenir escape)", category: "seguridad", essential: false },
  ],
  ave: [
    { name: "Jaula de transporte pequeña/cubierta", category: "transporte", essential: true },
    { name: "Tela oscura para cubrir jaula (reduce estrés)", category: "seguridad", essential: true },
    { name: "Semillas/alimento específico", category: "alimentacion", essential: true },
    { name: "Perchas portátiles", category: "otros", essential: false },
    { name: "Calentador portátil (aves tropicales)", category: "seguridad", essential: false },
  ],
  conejo: [
    { name: "Heno de timothy (alimentación base)", category: "alimentacion", essential: true },
    { name: "Verduras frescas para 3 días", category: "alimentacion", essential: true },
    { name: "Transportín bien ventilado", category: "transporte", essential: true },
    { name: "Sustrato/lecho absorbente", category: "higiene", essential: true },
    { name: "Botella de agua tipo biberón", category: "agua", essential: true },
  ],
  reptil: [
    { name: "Terrario de transporte seguro", category: "transporte", essential: true },
    { name: "Fuente de calor portátil/química", category: "seguridad", essential: true },
    { name: "Termómetro de terrario", category: "primeros_auxilios", essential: true },
    { name: "Alimento vivo o congelado según especie", category: "alimentacion", essential: true },
    { name: "Pulverizador para humedad", category: "higiene", essential: false },
  ],
  pez: [
    { name: "Recipiente de transporte con tapa", category: "transporte", essential: true },
    { name: "Oxigenador portátil/bomba a pilas", category: "seguridad", essential: true },
    { name: "Agua tratada/acondicionada de reserva", category: "agua", essential: true },
    { name: "Comida en escamas/pellets", category: "alimentacion", essential: true },
    { name: "Red para peces", category: "otros", essential: false },
  ],
  caballo: [
    { name: "Cabestro y ramal resistente", category: "transporte", essential: true },
    { name: "Heno/forraje para 5 días mínimo", category: "alimentacion", essential: true },
    { name: "Cubos para agua (mínimo 50L/día)", category: "agua", essential: true },
    { name: "Manta de caballo", category: "seguridad", essential: false },
    { name: "Botiquín equino específico", category: "primeros_auxilios", essential: true },
    { name: "Herraduras de repuesto/herramientas", category: "otros", essential: false },
    { name: "Rotulador/spray para marcar con teléfono", category: "seguridad", essential: true },
    { name: "Vendas de descanso", category: "primeros_auxilios", essential: false },
  ],
  ganado: [
    { name: "Identificación visible (crotales/marcas)", category: "documentacion", essential: true },
    { name: "Forraje/pienso para 5-7 días", category: "alimentacion", essential: true },
    { name: "Acceso a agua limpia (calculado por cabeza)", category: "agua", essential: true },
    { name: "Medicamentos veterinarios básicos", category: "medicacion", essential: true },
    { name: "Material de vallado de emergencia", category: "seguridad", essential: false },
    { name: "Plan de evacuación a zona alta/segura", category: "documentacion", essential: true },
    { name: "Contacto de veterinario de grandes animales", category: "documentacion", essential: true },
  ],
};

// ============================================
// CONSTANTES VITALES NORMALES POR ESPECIE
// (Para incluir en el kit de primeros auxilios)
// ============================================
const VITAL_SIGNS = {
  perro: {
    temperatura: "38-39°C",
    respiracion: "10-30 resp/min",
    pulso: "60-140 lat/min (según tamaño)",
    mucosas: "Rosadas, húmedas",
  },
  gato: {
    temperatura: "38-39.5°C",
    respiracion: "20-30 resp/min",
    pulso: "140-220 lat/min",
    mucosas: "Rosadas, húmedas",
  },
  conejo: {
    temperatura: "38.5-40°C",
    respiracion: "30-60 resp/min",
    pulso: "130-325 lat/min",
    mucosas: "Rosadas",
  },
  caballo: {
    temperatura: "37.5-38.5°C",
    respiracion: "8-16 resp/min",
    pulso: "28-44 lat/min",
    mucosas: "Rosa salmón",
  },
};

// Generate unique ID
let globalItemCounter = Date.now();
const generateUniqueId = (prefix = "item") => {
  globalItemCounter++;
  const random = Math.random().toString(36).substring(2, 10);
  return `${prefix}_${globalItemCounter}_${random}`;
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function MochilaVidaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checklists, setChecklists] = useState([]);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [activeChecklist, setActiveChecklist] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeKitType, setActiveKitType] = useState("evacuacion");
  const [showVitalSigns, setShowVitalSigns] = useState(false);

  // New checklist form
  const [newChecklistName, setNewChecklistName] = useState("");
  const [newKitType, setNewKitType] = useState("evacuacion");
  const [selectedAnimalTypes, setSelectedAnimalTypes] = useState(["perro"]);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    loadChecklists();
  }, [user]);

  const loadChecklists = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("ea_token");
      const res = await axios.get(`${API}/prepara/checklists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const fixedChecklists = (res.data || []).map(checklist => {
        if (!checklist.items) return checklist;
        
        const seenIds = new Set();
        const fixedItems = checklist.items.map(item => {
          if (!item.id || seenIds.has(item.id)) {
            return { ...item, id: generateUniqueId(item.category || 'item') };
          }
          seenIds.add(item.id);
          return item;
        });
        
        return { ...checklist, items: fixedItems };
      });
      
      setChecklists(fixedChecklists);
      
      if (fixedChecklists.length > 0 && !activeChecklist) {
        setActiveChecklist(fixedChecklists[0]);
        initializeExpandedCategories();
      }
    } catch (error) {
      console.error("Error loading checklists:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializeExpandedCategories = () => {
    const expanded = {};
    CATEGORIES.forEach(cat => {
      expanded[cat.id] = true;
    });
    setExpandedCategories(expanded);
  };

  const createNewChecklist = async () => {
    const kitType = KIT_TYPES.find(k => k.id === newKitType);
    const checklistName = newChecklistName.trim() || `${kitType?.label || 'Kit'} - ${new Date().toLocaleDateString()}`;

    setSaving(true);
    try {
      const items = [];
      const kitItems = KIT_ITEMS[newKitType] || {};
      
      // Add kit-specific items
      Object.entries(kitItems).forEach(([category, categoryItems]) => {
        categoryItems.forEach((item) => {
          items.push({
            id: generateUniqueId(category),
            name: item.name,
            category,
            is_completed: false,
            quantity: "",
            expiry_date: "",
            essential: item.essential || false,
            hasExpiry: item.hasExpiry || false,
          });
        });
      });

      // Add animal-specific items
      selectedAnimalTypes.forEach(animalType => {
        const animalItems = ANIMAL_SPECIFIC_ITEMS[animalType] || [];
        animalItems.forEach(item => {
          // Avoid duplicates
          const exists = items.some(i => i.name === item.name);
          if (!exists) {
            items.push({
              id: generateUniqueId(item.category),
              name: `${ANIMAL_TYPES.find(a => a.id === animalType)?.icon || ''} ${item.name}`,
              category: item.category,
              is_completed: false,
              quantity: "",
              expiry_date: "",
              essential: item.essential || false,
              animalType: animalType,
            });
          }
        });
      });

      const token = localStorage.getItem("ea_token");
      const res = await axios.post(
        `${API}/prepara/checklists`,
        {
          name: checklistName,
          kit_type: newKitType,
          animal_types: selectedAnimalTypes,
          items,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${kitType?.label} creado correctamente`);
      setShowNewDialog(false);
      setNewChecklistName("");
      setSelectedAnimalTypes(["perro"]);
      loadChecklists();
      
      // Set as active
      if (res.data) {
        setActiveChecklist({ ...res.data, items });
        initializeExpandedCategories();
      }
    } catch (error) {
      console.error("Error creating checklist:", error);
      toast.error("Error al crear el kit");
    } finally {
      setSaving(false);
    }
  };

  const toggleItem = async (itemId) => {
    if (!activeChecklist) return;

    const updatedItems = activeChecklist.items.map(item =>
      item.id === itemId ? { ...item, is_completed: !item.is_completed } : item
    );

    const updatedChecklist = { ...activeChecklist, items: updatedItems };
    setActiveChecklist(updatedChecklist);

    // Save to backend
    try {
      const token = localStorage.getItem("ea_token");
      await axios.put(
        `${API}/prepara/checklists/${activeChecklist.checklist_id}`,
        { items: updatedItems },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error("Error updating checklist:", error);
    }
  };

  const deleteChecklist = async (checklistId) => {
    if (!window.confirm("¿Eliminar este kit? Esta acción no se puede deshacer.")) return;

    try {
      const token = localStorage.getItem("ea_token");
      await axios.delete(`${API}/prepara/checklists/${checklistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Kit eliminado");
      if (activeChecklist?.checklist_id === checklistId) {
        setActiveChecklist(null);
      }
      loadChecklists();
    } catch (error) {
      console.error("Error deleting checklist:", error);
      toast.error("Error al eliminar");
    }
  };

  const getProgress = () => {
    if (!activeChecklist?.items?.length) return 0;
    const completed = activeChecklist.items.filter(i => i.is_completed).length;
    return Math.round((completed / activeChecklist.items.length) * 100);
  };

  const getCategoryProgress = (categoryId) => {
    if (!activeChecklist?.items) return { completed: 0, total: 0 };
    const categoryItems = activeChecklist.items.filter(i => i.category === categoryId);
    const completed = categoryItems.filter(i => i.is_completed).length;
    return { completed, total: categoryItems.length };
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const toggleAnimalType = (animalId) => {
    setSelectedAnimalTypes(prev => {
      if (prev.includes(animalId)) {
        return prev.filter(id => id !== animalId);
      } else {
        return [...prev, animalId];
      }
    });
  };

  // Export to PDF
  const exportToPDF = () => {
    if (!activeChecklist) return;

    const doc = new jsPDF();
    const kitType = KIT_TYPES.find(k => k.id === activeChecklist.kit_type);
    
    let yPos = 20;
    const margin = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);

    // Header
    doc.setFillColor(129, 128, 26);
    doc.rect(0, 0, pageWidth, 30, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    doc.text(normalizeForPDF(activeChecklist.name), margin, 18);
    
    doc.setFontSize(10);
    doc.text(normalizeForPDF(`Emergencia Animal - ${kitType?.description || ''}`), margin, 26);
    
    yPos = 45;
    
    // Progress
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.text(`Progreso: ${getProgress()}% completado`, margin, yPos);
    yPos += 15;

    // Items by category
    CATEGORIES.forEach(category => {
      const categoryItems = activeChecklist.items?.filter(i => i.category === category.id) || [];
      if (categoryItems.length === 0) return;

      if (yPos > 260) {
        doc.addPage();
        yPos = 20;
      }

      doc.setFillColor(245, 245, 240);
      doc.rect(margin - 5, yPos - 5, contentWidth + 10, 10, 'F');
      
      doc.setTextColor(129, 128, 26);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text(normalizeForPDF(category.label.toUpperCase()), margin, yPos + 3);
      yPos += 15;

      categoryItems.forEach(item => {
        if (yPos > 275) {
          doc.addPage();
          yPos = 20;
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        
        const checkbox = item.is_completed ? "[X]" : "[ ]";
        doc.setTextColor(item.is_completed ? 150 : 30, item.is_completed ? 150 : 41, item.is_completed ? 150 : 59);
        
        const normalizedName = normalizeForPDF(item.name);
        const lines = doc.splitTextToSize(`${checkbox} ${normalizedName}`, contentWidth - 10);
        lines.forEach(line => {
          doc.text(line, margin + 5, yPos);
          yPos += 5;
        });
        
        if (item.essential && !item.is_completed) {
          doc.setFontSize(7);
          doc.setTextColor(180, 120, 20);
          doc.text("ESENCIAL", margin + 8, yPos);
        }
        yPos += 4;
      });
      
      yPos += 5;
    });

    // Vital signs
    if (activeChecklist.kit_type === "primeros_auxilios") {
      doc.addPage();
      yPos = 20;
      
      doc.setFillColor(220, 38, 38);
      doc.rect(0, 0, pageWidth, 25, 'F');
      
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text("CONSTANTES VITALES NORMALES", margin, 16);
      
      yPos = 35;
      
      Object.entries(VITAL_SIGNS).forEach(([animal, signs]) => {
        doc.setTextColor(30, 41, 59);
        doc.setFontSize(11);
        doc.setFont("helvetica", "bold");
        doc.text(animal.charAt(0).toUpperCase() + animal.slice(1), margin, yPos);
        yPos += 6;
        
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        Object.entries(signs).forEach(([key, value]) => {
          doc.text(`• ${key}: ${value}`, margin + 5, yPos);
          yPos += 5;
        });
        yPos += 5;
      });
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-ES')} - Emergencia Animal`, margin, 285);

    const fileName = `${activeChecklist.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    toast.success("PDF descargado");
  };

  if (!user) return null;

  return (
    <div className="min-h-screen" data-testid="mochila-vida-page" style={{ background: C.white, fontFamily: BODY }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ background: C.navy }}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/prepara/panel")}
                className="flex items-center gap-2 text-sm font-semibold hover:opacity-80"
                style={{ fontFamily: BODY, color: C.blue, background: "none", border: "none", cursor: "pointer" }}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4" />
                Panel
              </button>
              <div className="h-5 w-px" style={{ background: "rgba(255,255,255,0.15)" }} />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center" style={{ background: C.olive, borderRadius: 4 }}>
                  <Backpack className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h1 className="text-sm font-bold" style={{ fontFamily: BODY, color: C.white }}>Kits de emergencia</h1>
                  <p className="text-[9px] tracking-[0.15em] uppercase" style={{ fontFamily: MONO, color: C.olive }}>Casa · Evacuación · Primeros auxilios</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {activeChecklist && (
                <button
                  onClick={exportToPDF}
                  className="px-3 py-1.5 text-[10px] font-semibold tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                  style={{ fontFamily: MONO, color: C.olive, border: `1px solid ${C.olive}60`, borderRadius: 4, background: "none", cursor: "pointer" }}
                  data-testid="download-pdf-btn"
                >
                  <FileDown className="w-3.5 h-3.5" />
                  PDF
                </button>
              )}
              <button
                onClick={() => setShowNewDialog(true)}
                className="px-4 py-1.5 text-[10px] font-semibold tracking-wider flex items-center gap-1.5 hover:-translate-y-0.5 transition-all"
                style={{ fontFamily: MONO, background: C.olive, color: C.white, borderRadius: 4, border: "none", cursor: "pointer" }}
                data-testid="new-kit-btn"
              >
                <Plus className="w-3.5 h-3.5" />
                Nuevo kit
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Kit Type Selector - BRUTALIST */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          {KIT_TYPES.map((kit) => (
            <div
              key={kit.id}
              className="cursor-pointer transition-all hover:-translate-y-1"
              onClick={() => setActiveKitType(kit.id)}
              style={{
                border: B.border,
                boxShadow: activeKitType === kit.id ? B.shadow : B.shadowSm,
                background: activeKitType === kit.id ? "#fff" : "#f8f7f4",
                borderLeft: activeKitType === kit.id ? `6px solid ${kit.color}` : B.border,
              }}
              data-testid={`kit-type-${kit.id}`}
            >
              <div className="p-4 text-center">
                <span className="text-3xl mb-2 block">{kit.icon}</span>
                <h3 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>{kit.label}</h3>
                <p className="text-[10px] mt-1" style={{ fontFamily: B.mono, color: "#2D356199" }}>{kit.description}</p>
                <span className="inline-block mt-2 px-3 py-0.5 text-[10px] font-bold" style={{ fontFamily: B.mono, border: `2px solid ${kit.color}`, color: kit.color }}>
                  {kit.duration}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Existing Checklists - BRUTALIST */}
        {checklists.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-bold mb-4" style={{ fontFamily: B.font, color: "#2D3561" }}>
              Tus Kits
            </h2>
            <div className="grid gap-3">
              {checklists.map((checklist) => {
                const kitType = KIT_TYPES.find(k => k.id === checklist.kit_type);
                const progress = checklist.items?.length 
                  ? Math.round((checklist.items.filter(i => i.is_completed).length / checklist.items.length) * 100)
                  : 0;
                const isActive = activeChecklist?.checklist_id === checklist.checklist_id;
                
                return (
                  <div
                    key={checklist.checklist_id}
                    className="cursor-pointer transition-all hover:-translate-y-0.5"
                    onClick={() => {
                      setActiveChecklist(checklist);
                      initializeExpandedCategories();
                    }}
                    style={{
                      border: B.border,
                      boxShadow: isActive ? B.shadow : "none",
                      background: isActive ? "#fff" : "#f8f7f4",
                      borderLeft: isActive ? `6px solid ${kitType?.color || "#8B9A2D"}` : B.border,
                    }}
                    data-testid={`checklist-${checklist.checklist_id}`}
                  >
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{kitType?.icon || ''}</span>
                        <div>
                          <h3 className="text-base font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                            {checklist.name}
                          </h3>
                          <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                            {checklist.items?.length || 0} items · {progress}% completado
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-24 h-2.5" style={{ background: "#2D356115", border: "2px solid #2D3561" }}>
                          <div className="h-full" style={{ width: `${progress}%`, background: "#686af3" }} />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteChecklist(checklist.checklist_id);
                          }}
                          className="w-8 h-8 flex items-center justify-center transition-all hover:bg-red-100"
                          style={{ border: "2px solid #e8637a", color: "#e8637a" }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active Checklist Detail */}
        {activeChecklist ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Checklist Header - BRUTALIST */}
            <div className="mb-6 overflow-hidden" style={{ border: B.border, boxShadow: B.shadow, background: "#fff" }}>
              <div className="h-3" style={{ background: `linear-gradient(90deg, #8B9A2D, #a09f2a)` }} />
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                    {activeChecklist.name}
                  </h2>
                  <p className="text-sm" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                    {activeChecklist.items?.length || 0} items en total
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold" style={{ fontFamily: B.font, color: "#8B9A2D" }}>
                    {getProgress()}%
                  </p>
                  <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>preparado</p>
                </div>
              </div>
              <div className="mx-6 mb-6 h-3" style={{ background: "#2D356115", border: "2px solid #2D3561" }}>
                <div className="h-full transition-all" style={{ width: `${getProgress()}%`, background: "#686af3" }} />
              </div>
            </div>

            {/* Vital Signs - BRUTALIST */}
            {activeChecklist.kit_type === "primeros_auxilios" && (
              <div className="mb-6" style={{ border: B.border, background: "#fff4f4", borderLeft: "6px solid #e8637a" }}>
                <div className="p-4">
                  <div 
                    className="flex items-center justify-between cursor-pointer"
                    onClick={() => setShowVitalSigns(!showVitalSigns)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center" style={{ background: "#e8637a", border: "2px solid #2D3561" }}>
                        <Activity className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                          Constantes Vitales Normales
                        </h3>
                        <p className="text-xs" style={{ fontFamily: B.mono, color: "#e8637a" }}>
                          Referencia rápida para detectar emergencias
                        </p>
                      </div>
                    </div>
                    <ChevronDown className={`w-5 h-5 transition-transform ${showVitalSigns ? 'rotate-180' : ''}`} style={{ color: "#e8637a" }} />
                  </div>
                  
                  <AnimatePresence>
                    {showVitalSigns && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pt-4 grid grid-cols-2 gap-4"
                        style={{ borderTop: "2px solid #e8637a44" }}
                      >
                        {Object.entries(VITAL_SIGNS).map(([animal, signs]) => (
                          <div key={animal} className="p-3" style={{ background: "#fff", border: "2px solid #2D3561" }}>
                            <h4 className="text-sm font-bold mb-2 capitalize" style={{ fontFamily: B.font, color: "#2D3561" }}>
                              {ANIMAL_TYPES.find(a => a.id === animal)?.icon} {animal}
                            </h4>
                            <ul className="space-y-1">
                              {Object.entries(signs).map(([key, value]) => (
                                <li key={key} className="text-xs" style={{ color: "#2D356199" }}>
                                  <span className="font-bold capitalize" style={{ color: "#2D3561" }}>{key}:</span> {value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            )}

            {/* Categories - BRUTALIST */}
            <div className="space-y-4">
              {CATEGORIES.map((category) => {
                const categoryItems = activeChecklist.items?.filter(i => i.category === category.id) || [];
                if (categoryItems.length === 0) return null;
                
                const { completed, total } = getCategoryProgress(category.id);
                const isExpanded = expandedCategories[category.id];
                const CategoryIcon = category.icon;

                return (
                  <div key={category.id} className="overflow-hidden" style={{ border: B.border, background: "#fff" }}>
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer transition-all hover:bg-[#f8f7f4]"
                      onClick={() => toggleCategory(category.id)}
                      data-testid={`category-${category.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 flex items-center justify-center"
                          style={{ background: `${category.color}20`, border: `2px solid ${category.color}` }}
                        >
                          <CategoryIcon className="w-5 h-5" style={{ color: category.color }} />
                        </div>
                        <div>
                          <h3 className="text-base font-bold" style={{ fontFamily: B.font, color: "#2D3561" }}>
                            {category.label}
                          </h3>
                          <p className="text-xs" style={{ fontFamily: B.mono, color: "#2D356188" }}>
                            {completed} de {total} completados
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-20 h-2.5" style={{ background: "#2D356115", border: "2px solid #2D3561" }}>
                          <div className="h-full" style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%`, background: "#686af3" }} />
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5" style={{ color: "#2D356188" }} />
                        ) : (
                          <ChevronDown className="w-5 h-5" style={{ color: "#2D356188" }} />
                        )}
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          style={{ borderTop: "2px solid #2D3561" }}
                        >
                          <div className="p-4 space-y-2">
                            {categoryItems.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-start gap-3 p-3 transition-all"
                                style={{
                                  background: item.is_completed ? "#f0fff4" : item.essential ? "#fffceb" : "#fff",
                                  border: `2px solid ${item.is_completed ? "#8B9A2D" : item.essential ? "#f0a030" : "#2D356133"}`,
                                }}
                                data-testid={`item-${item.id}`}
                              >
                                <Checkbox
                                  checked={item.is_completed}
                                  onCheckedChange={() => toggleItem(item.id)}
                                  className="mt-0.5 data-[state=checked]:bg-[#8B9A2D] data-[state=checked]:border-[#8B9A2D]"
                                />
                                <div className="flex-1">
                                  <p className={`text-sm ${item.is_completed ? 'line-through' : ''}`}
                                    style={{ fontFamily: B.font, color: item.is_completed ? "#2D356166" : "#2D3561" }}>
                                    {item.name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {item.essential && !item.is_completed && (
                                      <span className="text-[10px] font-bold px-2 py-0.5" style={{ fontFamily: B.mono, background: "#f0a030", color: "#fff", border: "1px solid #2D3561" }}>
                                        Esencial
                                      </span>
                                    )}
                                    {item.hasExpiry && (
                                      <span className="text-[10px] font-bold px-2 py-0.5" style={{ fontFamily: B.mono, background: "#686af320", color: "#686af3", border: "1px solid #686af3" }}>
                                        Revisar caducidad
                                      </span>
                                    )}
                                    {item.animalType && (
                                      <span className="text-[10px] font-bold px-2 py-0.5" style={{ fontFamily: B.mono, background: "#a88efb20", color: "#a88efb", border: "1px solid #a88efb" }}>
                                        {ANIMAL_TYPES.find(a => a.id === item.animalType)?.label}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        ) : (
          /* Empty State - BRUTALIST */
          <div className="p-12 text-center" style={{ border: B.border, background: "#fff", boxShadow: B.shadow }}>
            <div className="relative w-24 h-24 mx-auto mb-6">
              <img 
                src="/avatars/avatar-purple.png" 
                alt="Animal"
                className="w-full h-full object-contain opacity-80"
              />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              No tienes ningún kit creado
            </h2>
            <p className="text-sm mb-6" style={{ fontFamily: B.mono, color: "#2D356188" }}>
              Crea tu primer kit de emergencia para estar preparado
            </p>
            <Button
              onClick={() => setShowNewDialog(true)}
              className="font-bold uppercase tracking-wider"
              style={{ background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm, borderRadius: 0 }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear mi primer kit
            </Button>
          </div>
        )}
      </main>

      {/* New Checklist Dialog - BRUTALIST STYLE */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent className="max-w-lg" style={{ borderRadius: 0, border: B.border, boxShadow: B.shadow }}>
          <DialogHeader>
            <DialogTitle className="text-lg font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: B.font, color: "#2D3561" }}>
              <div className="w-7 h-7 flex items-center justify-center" style={{ background: "#8B9A2D", border: "2px solid #2D3561" }}>
                <Plus className="w-4 h-4 text-white" />
              </div>
              CREAR NUEVO KIT
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Kit Type Selection */}
            <div>
              <Label className="text-xs font-bold tracking-widest mb-3 block" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                TIPO DE KIT
              </Label>
              <div className="grid grid-cols-3 gap-3">
                {KIT_TYPES.map((kit) => (
                  <div
                    key={kit.id}
                    className="p-3 cursor-pointer text-center transition-all hover:-translate-y-0.5"
                    style={{
                      border: newKitType === kit.id ? "3px solid #8B9A2D" : "2px solid #2D3561",
                      background: newKitType === kit.id ? "#8B9A2D15" : "#fff",
                      boxShadow: newKitType === kit.id ? B.shadowSm : "none",
                    }}
                    onClick={() => setNewKitType(kit.id)}
                    data-testid={`select-kit-${kit.id}`}
                  >
                    <span className="text-2xl block mb-1">{kit.icon}</span>
                    <p className="text-[10px] font-bold tracking-wider" style={{ fontFamily: B.mono, color: "#2D3561" }}>{kit.label.toUpperCase()}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Animal Types */}
            <div>
              <Label className="text-xs font-bold tracking-widest mb-3 block" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                TIPOS DE ANIMALES
              </Label>
              <p className="text-[10px] mb-2" style={{ fontFamily: B.mono, color: "#a88efb" }}>// PUEDES SELECCIONAR VARIOS</p>
              <div className="grid grid-cols-4 gap-2">
                {ANIMAL_TYPES.map((animal) => (
                  <div
                    key={animal.id}
                    className="p-2 cursor-pointer text-center transition-all hover:-translate-y-0.5"
                    style={{
                      border: selectedAnimalTypes.includes(animal.id) ? "3px solid #8B9A2D" : "2px solid #2D3561",
                      background: selectedAnimalTypes.includes(animal.id) ? "#8B9A2D15" : "#fff",
                    }}
                    onClick={() => toggleAnimalType(animal.id)}
                    data-testid={`select-animal-${animal.id}`}
                  >
                    <span className="text-xl block">{animal.icon}</span>
                    <p className="text-[9px] font-bold" style={{ fontFamily: B.mono, color: "#2D3561" }}>{animal.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Name */}
            <div>
              <Label className="text-xs font-bold tracking-widest mb-2 block" style={{ fontFamily: B.mono, color: "#2D3561" }}>
                NOMBRE (OPCIONAL)
              </Label>
              <Input
                value={newChecklistName}
                onChange={(e) => setNewChecklistName(e.target.value)}
                placeholder={`${KIT_TYPES.find(k => k.id === newKitType)?.label} de mis animales`}
                style={{ border: "2px solid #2D3561", borderRadius: 0 }}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button
              onClick={() => setShowNewDialog(false)}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5"
              style={{ fontFamily: B.mono, background: "#fff", color: "#2D3561", border: B.border }}
            >
              CANCELAR
            </button>
            <button
              onClick={createNewChecklist}
              disabled={saving || selectedAnimalTypes.length === 0}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontFamily: B.mono, background: "#8B9A2D", color: "#fff", border: B.border, boxShadow: B.shadowSm }}
              data-testid="create-kit-btn"
            >
              {saving ? "CREANDO..." : "CREAR KIT"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
